// src/components/Dashboard.js
import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useParams, Link, useLocation } from 'react-router-dom';
import apiService from '../services/api';
import { useAuth } from '../contexts/CognitoAuthContext';
import { ROUTES, TAB_LABELS, INTERNAL_CLIENT_ID } from '../constants/routes';
import { formatEmailDisplayName, getInitialsFromEmail } from '../utils';
import { isUuid } from '../utils';

// Entity Navigation
import EntityNavigation, { EntityNavigationProvider } from './EntityNavigation';

// Header component - now only shows on list views (entity nav handles entity detail pages)
const DashboardHeader = ({ getHeaderTitle, user, signOut, isEntityDetailPage }) => {
    // Hide header on entity detail pages (EntityNavigation handles those)
    if (isEntityDetailPage) {
        return null;
    }
    
    return (
        <header className="sticky top-0 z-10" style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-sm)' }}>
            <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-3 flex justify-between items-center">
                <h1 className="text-xl md:text-2xl font-bold truncate" style={{ color: 'var(--pallium-primary-purple)' }}>
                    {getHeaderTitle()}
                </h1>
                <UserDropdown user={user} signOut={signOut} />
            </div>
        </header>
    );
};

// Feature flags: Set to false to hide specific tabs
const SHOW_KNOWLEDGE_GRAPH = false;
const SHOW_AI_INSIGHTS = false;
const SHOW_COMMS_AND_SENTIMENT_CLIENT = false;  // for client/account view
const SHOW_COMMS_AND_SENTIMENT_DEPARTMENT = true;  // for department view

// Sidebar collapse state key for localStorage
const SIDEBAR_COLLAPSED_KEY = 'cortex-sidebar-collapsed';
// Date range persistence key
const DATE_RANGE_KEY = 'cortex-date-range';

// Helper to get default date range (last 30 days)
const getDefaultDateRange = () => ({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
});

// Helper to get persisted date range
const getPersistedDateRange = () => {
    try {
        const stored = localStorage.getItem(DATE_RANGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            // Validate the dates are valid
            if (parsed.start && parsed.end) {
                return parsed;
            }
        }
    } catch (e) {
        console.error('Error reading persisted date range:', e);
    }
    return getDefaultDateRange();
};

// User Dropdown Component
const UserDropdown = ({ user, signOut }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Get user display name from email
    const getUserDisplayName = () => formatEmailDisplayName(user?.email, 'User');

    // Get user initials for avatar
    const getUserInitials = () => getInitialsFromEmail(user?.email, 'U');

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Trigger Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all duration-200"
                style={{ 
                    backgroundColor: isOpen ? 'var(--pallium-purple-0-5)' : 'transparent',
                }}
                onMouseOver={(e) => { if (!isOpen) e.currentTarget.style.backgroundColor = 'var(--pallium-purple-0-5)'; }}
                onMouseOut={(e) => { if (!isOpen) e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
                <span className="text-sm font-medium hidden md:block" style={{ color: 'var(--pallium-text-primary)' }}>
                    {getUserDisplayName()}
                </span>
                {/* User Avatar */}
                <div 
                    className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0"
                    style={{ 
                        backgroundColor: 'var(--pallium-primary-purple)', 
                        color: 'var(--pallium-white)',
                        border: '2px solid var(--pallium-purple-2)'
                    }}
                >
                    {getUserInitials()}
                </div>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div 
                    className="absolute right-0 mt-2 w-64 rounded-xl overflow-hidden z-50"
                    style={{ 
                        backgroundColor: 'var(--pallium-white)', 
                        boxShadow: 'var(--pallium-shadow-lg)',
                        border: '1px solid var(--pallium-stroke-light)'
                    }}
                >
                    {/* User Info Header */}
                    <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--pallium-stroke-light)' }}>
                        <div className="flex items-center gap-3">
                            <div 
                                className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0"
                                style={{ 
                                    backgroundColor: 'var(--pallium-primary-purple)', 
                                    color: 'var(--pallium-white)'
                                }}
                            >
                                {getUserInitials()}
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-semibold truncate" style={{ color: 'var(--pallium-text-primary)' }}>
                                    {getUserDisplayName()}
                                </p>
                                <p className="text-xs truncate" style={{ color: 'var(--pallium-text-secondary)' }}>
                                    {user?.email}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Sign Out */}
                    <div className="py-1">
                        <button
                            onClick={() => { setIsOpen(false); signOut(); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150"
                            style={{ color: 'var(--pallium-text-primary)' }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--pallium-purple-0-5)'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            <svg className="w-5 h-5" style={{ color: 'var(--pallium-icon-gray)' }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                            </svg>
                            Sign Out
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Sidebar sub-navigation component - hidden when on entity detail pages (EntityNavigation handles those)
const SidebarSubNav = ({ 
    clientId, 
    departmentId, 
    mainPathSegment, 
    currentSubView, 
    sidebarCollapsed,
    clientSpecificNavItems,
    departmentSpecificNavItems,
    getClientDisplayName,
    allDepartmentIds
}) => {
    // Hide when on entity detail pages (EntityNavigation now handles tab navigation)
    // This component is only used for list pages, so return null if we're on a detail page
    if (clientId || departmentId) {
        return null;
    }
    
    // This component no longer renders navigation items since EntityNavigation handles
    // all entity detail page navigation. The JSX below was unreachable dead code.
    return null;
};

const Dashboard = () => {
    const { clientId, staffId, departmentId } = useParams();

    const location = useLocation();
    const { user, hasRole, signOut } = useAuth();

    const [apiConfigured, setApiConfigured] = useState(false);
    const [allClients, setAllClients] = useState([]); // Store full client objects with clientId and name
    const [allClientIds, setAllClientIds] = useState([]);
    const [allDepartmentIds, setAllDepartmentIds] = useState([]);
    const [allStaff, setAllStaff] = useState([]); // Store staff list for entity navigation
    const [loading, setLoading] = useState(true);
    const [clientsLoaded, setClientsLoaded] = useState(false); // Track if client list has been fetched
    const [fetchingClientIds, setFetchingClientIds] = useState(new Set()); // Track which clients are being fetched
    const [error, setError] = useState(null);
    
    // Date range - initialized from localStorage
    const [dateRange, setDateRangeState] = useState(getPersistedDateRange);
    
    // Wrapper to persist date range changes
    const setDateRange = (newRange) => {
        setDateRangeState(newRange);
        localStorage.setItem(DATE_RANGE_KEY, JSON.stringify(newRange));
    };
    
    // Sidebar collapsed state - initialize from localStorage
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
        return saved === 'true';
    });
    
    // Toggle sidebar and persist state
    const toggleSidebar = () => {
        setSidebarCollapsed(prev => {
            const newState = !prev;
            localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(newState));
            return newState;
        });
    };

    // Check if user is admin using the auth context
    const isAdminUser = user && hasRole(['admin']);

    useEffect(() => {
        const connectAndFetchInitialData = async () => {
            if (apiConfigured) {
                // If already configured, potentially re-fetch IDs if path changes type (client vs internal)
                // For simplicity now, we assume this is mainly for initial setup.
                // A more robust solution might refetch IDs if mainPathSegment changes significantly.
                setLoading(false); 
                return;
            }
            setLoading(true); setError(null); setApiConfigured(false);
            setClientsLoaded(false); // Reset so entity navigation shows "Loading..." during refetch
            try {
                const success = await apiService.initialize();

                if (success) {
                    setApiConfigured(true);
                    const [clientResult, deptResult, staffResult] = await Promise.allSettled([
                        apiService.getClientList(),
                        apiService.getDepartmentList(),
                        apiService.getStaffPerformanceSummaryList()
                    ]);

                    if (clientResult.status === 'fulfilled') {
                        const fetchedClients = clientResult.value || [];
                        setAllClients(fetchedClients);
                        const clientIdsList = fetchedClients.map(r => r.clientId);
                        setAllClientIds(clientIdsList);
                        setClientsLoaded(true);
                        if (clientIdsList.length === 0 && location.pathname.startsWith(ROUTES.ACCOUNTS) && !location.pathname.endsWith(ROUTES.ACCOUNTS)) {
                             setError("No clients found in the database.");
                        }
                    } else {
                        console.error("Failed to fetch client ID list:", clientResult.reason);
                        setError("Failed to fetch client ID list.");
                        setClientsLoaded(true); // Mark as loaded even on error to prevent infinite loading
                    }

                    if (deptResult.status === 'fulfilled') {
                        const fetchedDepts = deptResult.value || [];
                        const departmentIdsList = fetchedDepts.map(r => r.department).filter(d => d); // Filter out null/empty
                        setAllDepartmentIds(departmentIdsList);
                         if (departmentIdsList.length === 0 && location.pathname.startsWith(ROUTES.OPERATIONS) && !location.pathname.endsWith(ROUTES.OPERATIONS)) { // Don't error on /operations list view
                             setError("No departments found. Ensure staff nodes have a 'department' property.");
                        }
                    } else {
                        console.error("Failed to fetch department ID list:", deptResult.reason);
                        // Don't set error for department fetch failure on non-operations pages
                        if (location.pathname.startsWith(ROUTES.OPERATIONS)) {
                            setError("Failed to fetch department ID list.");
                        }
                    }

                    if (staffResult.status === 'fulfilled') {
                        setAllStaff(staffResult.value || []);
                    } else {
                        console.error("Failed to fetch staff list:", staffResult.reason);
                        // Don't set error for staff fetch failure - non-critical
                    }
                } else {
                    setError('Failed to connect to backend service. Check if the service is running.');
                }
            } catch (err) {
                setError(`Connection error: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };
        connectAndFetchInitialData();
    }, [location.pathname, apiConfigured]); // Re-run if path changes, to fetch correct IDs

    // Effect to fetch client info when current clientId is not in the preloaded list
    useEffect(() => {
        let isMounted = true;
        const fetchMissingClient = async () => {
            if (!clientId || !clientsLoaded || clientId === INTERNAL_CLIENT_ID) return;
            
            // Check if client is already in the list
            const existingClient = allClients.find(c => c.clientId === clientId);
            if (existingClient) return;
            
            // Check if already fetching this client
            if (fetchingClientIds.has(clientId)) return;
            
            // Mark as fetching
            if (isMounted) setFetchingClientIds(prev => new Set(prev).add(clientId));
            
            // Try to fetch this specific client with retry logic
            let retries = 3;
            while (retries > 0 && isMounted) {
                try {
                    const fetchedClient = await apiService.getClientById(clientId);
                    if (fetchedClient && fetchedClient.clientId) {
                        if (!isMounted) return;
                        // Add to allClients so the name can be displayed
                        setAllClients(prev => {
                            // Check if already added (race condition)
                            if (prev.find(c => c.clientId === fetchedClient.clientId)) {
                                return prev;
                            }
                            return [...prev, fetchedClient];
                        });
                        setAllClientIds(prev => {
                            if (prev.includes(fetchedClient.clientId)) {
                                return prev;
                            }
                            return [...prev, fetchedClient.clientId];
                        });
                        setError(null); // Clear any "invalid client" error
                        break; // Success, exit retry loop
                    } else {
                        retries--;
                    }
                } catch (err) {
                    if (!isMounted) return;
                    console.warn(`Could not fetch client ${clientId} (${retries} retries left):`, err.message);
                    retries--;
                    if (retries > 0 && isMounted) {
                        // Wait before retrying (exponential backoff)
                        const delay = 500 * (4 - retries);
                        await new Promise(resolve => setTimeout(resolve, delay));
                    }
                }
            }
            
            // Remove from fetching set
            if (isMounted) {
                setFetchingClientIds(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(clientId);
                    return newSet;
                });
            }
        };
        
        fetchMissingClient();
        
        return () => {
            isMounted = false;
        };
    }, [clientId, clientsLoaded, allClients, fetchingClientIds]);

    useEffect(() => {
        // Validate clientId only if we are on a client-specific path and have fetched client IDs
        // Skip validation for the special "internal" client ID
        // Note: We no longer immediately error on missing clients, as we try to fetch them first
        if (clientId && clientId !== INTERNAL_CLIENT_ID && allClientIds.length > 0 && location.pathname.startsWith(ROUTES.ACCOUNTS)) {
            if (!allClientIds.includes(clientId)) {
                // Don't immediately error - the fetchMissingClient effect will try to load it
                // Only show error after a delay if still not found
            } else {
                setError(null); // Clear any previous errors
            }
        } else if (clientId === INTERNAL_CLIENT_ID) {
            // Internal client is always valid
            setError(null);
        }
    }, [clientId, allClientIds, location.pathname]);

    useEffect(() => {
        // Validate departmentId only if we are on an internal path and have fetched department IDs
        if (departmentId && allDepartmentIds.length > 0 && location.pathname.startsWith(ROUTES.OPERATIONS)) {
            if (!allDepartmentIds.includes(departmentId)) {
                setError(`Invalid department ID: ${departmentId}. Available departments: ${allDepartmentIds.join(', ')}`);
            } else {
                setError(null); // Clear any previous errors
            }
        }
    }, [departmentId, allDepartmentIds, location.pathname]);

    // Header and Sidebar Logic (remains mostly the same, but with the new Issues tab)
    const pathSegments = location.pathname.split('/').filter(p => p);
    const mainPathSegment = pathSegments.length > 0 ? pathSegments[0] : 'accounts'; 

    let currentSubView = '';
    if (mainPathSegment === 'accounts' && clientId && pathSegments.length > 2) {
        currentSubView = pathSegments[2];
    } else if (mainPathSegment === 'operations' && departmentId && pathSegments.length > 2) {
        currentSubView = pathSegments[2];
    }


    // Helper to get client name, returns null if not yet loaded
    const getClientDisplayName = (cId) => {
        if (!cId) return null;
        if (!clientsLoaded) return 'Loading...'; // Show Loading while fetching
        
        // Check if we're currently fetching this client
        if (fetchingClientIds.has(cId)) return 'Loading...';
        
        const client = allClients.find(c => c.clientId === cId);
        // Use client name if available and not a UUID, otherwise show friendly fallback
        if (client?.name && !isUuid(client.name)) {
            return client.name;
        }
        // If we have the client but no valid name, still show unnamed
        if (client) {
            return '(Unnamed Client)';
        }
        // If clients are loaded but this specific client isn't found yet, show loading
        // (it might be fetching via fetchMissingClient)
        return 'Loading...';
    };

    const getHeaderTitle = () => {
        if (mainPathSegment === 'accounts') {
            const displayName = getClientDisplayName(clientId);
            if (clientId && !displayName) {
                return `${TAB_LABELS.ACCOUNTS} > Loading...`;
            }
            return `${TAB_LABELS.ACCOUNTS} ${clientId ? `> ${displayName}` : ''}`;
        }
        if (mainPathSegment === 'staff') {
            return `${TAB_LABELS.STAFF} ${staffId ? `> ${staffId}` : ''}`; // Assuming staff details might include a name
        }
        if (mainPathSegment === 'operations') {
            return `${TAB_LABELS.OPERATIONS} ${departmentId ? `> ${allDepartmentIds.find(d => d === departmentId) || departmentId}` : ''}`;
        }
        return 'Dashboard';
    };
    
    const clientSpecificNavItems = [
        { view: 'overview', label: 'Overview', path: `${ROUTES.ACCOUNTS}/${clientId}/overview`, icon: <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" /> },
        { view: 'goals', label: 'Goals & Initiatives', path: `${ROUTES.ACCOUNTS}/${clientId}/goals`, icon: <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" /> },
        { view: 'issues', label: 'Issues', path: `${ROUTES.ACCOUNTS}/${clientId}/issues`, icon: <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /> },
        { view: 'action-items', label: 'Action Items', path: `${ROUTES.ACCOUNTS}/${clientId}/action-items`, icon: <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /> },
        // START: Communications & Sentiment Tab - Remove this block to permanently remove the tab
        ...(SHOW_COMMS_AND_SENTIMENT_CLIENT ? [
            { view: 'sentiment', label: 'Comms & Sentiment', path: `${ROUTES.ACCOUNTS}/${clientId}/sentiment`, icon: <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z" clipRule="evenodd" /> }
        ] : []),
        // END: Communications & Sentiment Tab
        { type: 'divider' },
        // START: Knowledge Graph Tab - Remove this block to permanently remove the tab
        ...(SHOW_KNOWLEDGE_GRAPH ? [
            { view: 'graph', label: 'Knowledge Graph', path: `${ROUTES.ACCOUNTS}/${clientId}/graph`, icon: <path fillRule="evenodd" d="M5 3a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2H5zm9 4a1 1 0 10-2 0v6a1 1 0 102 0V7zm-3 2a1 1 0 10-2 0v4a1 1 0 102 0V9zm-3 3a1 1 0 10-2 0v1a1 1 0 102 0v-1z" clipRule="evenodd" /> }
        ] : []),
        // END: Knowledge Graph Tab
        // START: AI Insights Tab - Remove this block to permanently remove the tab
        ...(SHOW_AI_INSIGHTS ? [
            { view: 'insights', label: 'AI Insights', path: `${ROUTES.ACCOUNTS}/${clientId}/insights`, icon: <path fillRule="evenodd" d="M11 3a1 1 0 100 2h.01a1 1 0 100-2H11zM5 3a1 1 0 00-1 1v.01a1 1 0 001 1H6a1 1 0 001-1V4a1 1 0 00-1-1H5zM3 11a1 1 0 100 2h.01a1 1 0 100-2H3zM3 17a1 1 0 100 2h.01a1 1 0 100-2H3zM9 17a1 1 0 100 2h2a1 1 0 100-2H9zM11 13a1 1 0 100 2h.01a1 1 0 100-2H11zM15 3a1 1 0 100 2h.01a1 1 0 100-2H15zM15 9a1 1 0 100 2h.01a1 1 0 100-2H15zM15 15a1 1 0 100 2h.01a1 1 0 100-2H15z" clipRule="evenodd" /> }
        ] : []),
        // END: AI Insights Tab
    ];
    
    const departmentSpecificNavItems = [
        { view: 'overview', label: 'Overview', path: `${ROUTES.OPERATIONS}/${departmentId}/overview`, icon: <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" /> },
        { view: 'goals', label: 'Goals & Initiatives', path: `${ROUTES.OPERATIONS}/${departmentId}/goals`, icon: <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" /> },
        { view: 'action-items', label: 'Action Items', path: `${ROUTES.OPERATIONS}/${departmentId}/action-items`, icon: <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /> },
        { view: 'issues', label: 'Issues', path: `${ROUTES.OPERATIONS}/${departmentId}/issues`, icon: <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /> },
        // START: Communications & Sentiment Tab - Remove this block to permanently remove the tab
        ...(SHOW_COMMS_AND_SENTIMENT_DEPARTMENT ? [
            { view: 'sentiment', label: 'Comms & Sentiment', path: `${ROUTES.OPERATIONS}/${departmentId}/sentiment`, icon: <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z" clipRule="evenodd" /> }
        ] : []),
        // END: Communications & Sentiment Tab
    ];

     if (loading && !apiConfigured) { return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--pallium-primary-purple)' }}></div><span className="ml-3" style={{ color: 'var(--pallium-text-secondary)' }}>Connecting to Backend Service...</span></div>;}
    if (!apiConfigured && error && !loading) { return <div className="p-8 flex flex-col items-center justify-center h-screen" style={{ backgroundColor: 'var(--pallium-alert-error-bg)', color: 'var(--pallium-error-red)' }}><h2>Connection Error</h2><p>{error}</p><button onClick={() => window.location.reload()} className="mt-4 text-white py-2 px-4 rounded-md transition duration-200" style={{ backgroundColor: 'var(--pallium-button-primary-bg)' }} onMouseOver={(e) => e.target.style.backgroundColor = 'var(--pallium-button-primary-hover)'} onMouseOut={(e) => e.target.style.backgroundColor = 'var(--pallium-button-primary-bg)'}>Retry Connection</button></div>;}
    if (loading) { return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--pallium-primary-purple)' }}></div><span className="ml-3" style={{ color: 'var(--pallium-text-secondary)' }}>Setting up Dashboard...</span></div>;}


    // Check if we're on an entity detail page (for conditional header rendering)
    const isEntityDetailPage = clientId || staffId || departmentId;

    return (
        <div className="min-h-screen flex" style={{ backgroundColor: 'var(--pallium-stroke-lighter)' }}>
            {/* Sidebar - full height, fixed position */}
            <aside 
                className={`overflow-hidden fixed h-screen top-0 left-0 transition-all duration-300 flex flex-col z-20 ${sidebarCollapsed ? 'w-16' : 'w-64'}`}
                style={{ backgroundColor: 'var(--pallium-primary-dark-navy)', boxShadow: 'var(--pallium-shadow-md)' }}
            >
                {/* Logo section at top */}
                <div className={`flex items-center py-3 ${sidebarCollapsed ? 'justify-center px-2' : 'px-4'}`} style={{ borderBottom: '1px solid var(--pallium-dark-purple-4)' }}>
                    {sidebarCollapsed ? (
                        <img 
                            src="/pallium_logo.png" 
                            alt="Pallium" 
                            className="h-8 w-8 object-contain"
                            style={{ filter: 'brightness(0) invert(1)' }}
                        />
                    ) : (
                        <img 
                            src="/pallium-logo-white.svg" 
                            alt="Pallium" 
                            className="h-8 object-contain"
                        />
                    )}
                </div>

                {/* Sidebar collapse toggle */}
                <div className={`${sidebarCollapsed ? 'px-2' : 'px-2 md:px-4'} py-2`} style={{ borderBottom: '1px solid var(--pallium-dark-purple-4)' }}>
                    <button
                        onClick={toggleSidebar}
                        className={`w-full flex items-center px-2 py-2 md:px-3 md:py-2 rounded-md transition-colors duration-200 ${sidebarCollapsed ? 'justify-center' : ''}`}
                        style={{ color: 'var(--pallium-purple-1)' }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--pallium-dark-purple-4)'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        title={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
                    >
                        <svg 
                            className={`h-5 w-5 flex-shrink-0 transition-transform duration-200 ${sidebarCollapsed ? 'rotate-180' : ''}`}
                            xmlns="http://www.w3.org/2000/svg" 
                            viewBox="0 0 20 20" 
                            fill="currentColor"
                        >
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {!sidebarCollapsed && <span className="ml-2 text-sm">Collapse</span>}
                    </button>
                </div>
                
                <nav className={`py-4 flex-1 overflow-y-auto ${sidebarCollapsed ? 'px-2' : 'px-2 md:px-4'}`}>
                    <ul className="space-y-1 md:space-y-2">
                        <li>
                            <Link
                                to={ROUTES.ACCOUNTS}
                                title={TAB_LABELS.ACCOUNTS}
                                className={`w-full text-left px-2 py-2 md:px-3 md:py-2 rounded-md flex items-center text-sm md:text-base transition-colors duration-200 ${sidebarCollapsed ? 'justify-center' : ''}`}
                                style={mainPathSegment === 'accounts' && !clientId && !departmentId 
                                    ? { backgroundColor: 'var(--pallium-purple-6)', color: 'var(--pallium-white)', fontWeight: 600 } 
                                    : { color: 'var(--pallium-purple-1)' }}
                                onMouseOver={(e) => { if (!(mainPathSegment === 'accounts' && !clientId && !departmentId)) e.currentTarget.style.backgroundColor = 'var(--pallium-dark-purple-4)'; }}
                                onMouseOut={(e) => { if (!(mainPathSegment === 'accounts' && !clientId && !departmentId)) e.currentTarget.style.backgroundColor = 'transparent'; }}
                            >
                                <svg className={`h-5 w-5 flex-shrink-0 ${sidebarCollapsed ? '' : 'mr-3'}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" /></svg>
                                {!sidebarCollapsed && <span className="truncate whitespace-nowrap max-w-full">{TAB_LABELS.ACCOUNTS}</span>}
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/staff"
                                title={TAB_LABELS.STAFF}
                                className={`w-full text-left px-2 py-2 md:px-3 md:py-2 rounded-md flex items-center text-sm md:text-base transition-colors duration-200 ${sidebarCollapsed ? 'justify-center' : ''}`}
                                style={mainPathSegment === 'staff' 
                                    ? { backgroundColor: 'var(--pallium-purple-6)', color: 'var(--pallium-white)', fontWeight: 600 } 
                                    : { color: 'var(--pallium-purple-1)' }}
                                onMouseOver={(e) => { if (mainPathSegment !== 'staff') e.currentTarget.style.backgroundColor = 'var(--pallium-dark-purple-4)'; }}
                                onMouseOut={(e) => { if (mainPathSegment !== 'staff') e.currentTarget.style.backgroundColor = 'transparent'; }}
                            >
                                 <svg className={`h-5 w-5 flex-shrink-0 ${sidebarCollapsed ? '' : 'mr-3'}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" /></svg>
                                {!sidebarCollapsed && <span className="truncate whitespace-nowrap max-w-full">{TAB_LABELS.STAFF}</span>}
                            </Link>
                        </li>
                        {isAdminUser ? <li>
                            <Link
                                to={ROUTES.OPERATIONS}
                                title={TAB_LABELS.OPERATIONS}
                                className={`w-full text-left px-2 py-2 md:px-3 md:py-2 rounded-md flex items-center text-sm md:text-base transition-colors duration-200 ${sidebarCollapsed ? 'justify-center' : ''}`}
                                style={mainPathSegment === 'operations' && !departmentId 
                                    ? { backgroundColor: 'var(--pallium-purple-6)', color: 'var(--pallium-white)', fontWeight: 600 } 
                                    : { color: 'var(--pallium-purple-1)' }}
                                onMouseOver={(e) => { if (!(mainPathSegment === 'operations' && !departmentId)) e.currentTarget.style.backgroundColor = 'var(--pallium-dark-purple-4)'; }}
                                onMouseOut={(e) => { if (!(mainPathSegment === 'operations' && !departmentId)) e.currentTarget.style.backgroundColor = 'transparent'; }}
                            >
                               <svg className={`h-5 w-5 flex-shrink-0 ${sidebarCollapsed ? '' : 'mr-3'}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                </svg>
                                {!sidebarCollapsed && <span className="truncate whitespace-nowrap max-w-full">{TAB_LABELS.OPERATIONS}</span>}
                            </Link>
                        </li> : null}
                        
                        {/* Sidebar sub-nav - shows sub-navigation items for entity detail pages */}
                        <SidebarSubNav
                            clientId={clientId}
                            departmentId={departmentId}
                            mainPathSegment={mainPathSegment}
                            currentSubView={currentSubView}
                            sidebarCollapsed={sidebarCollapsed}
                            clientSpecificNavItems={clientSpecificNavItems}
                            departmentSpecificNavItems={departmentSpecificNavItems}
                            getClientDisplayName={getClientDisplayName}
                            allDepartmentIds={allDepartmentIds}
                        />
                    </ul>
                </nav>
            </aside>

            {/* Main content area - offset by sidebar width */}
            <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
                <DashboardHeader 
                    getHeaderTitle={getHeaderTitle} 
                    user={user} 
                    signOut={signOut}
                    isEntityDetailPage={isEntityDetailPage}
                />

            {error && !loading && ( 
                <div className="w-full px-4 sm:px-6 lg:px-8 py-2">
                    <div className="p-3 rounded-md" style={{ backgroundColor: 'var(--pallium-alert-error-bg)', borderLeft: '4px solid var(--pallium-error-red)', color: 'var(--pallium-error-red)' }}>
                        <p className="font-bold">Error:</p>
                        <p>{error}</p>
                    </div>
                </div> 
            )}

                <main className="flex-1 overflow-y-auto p-4 md:p-8">
                    {apiConfigured ? (
                        <EntityNavigationProvider>
                            {/* Entity Navigation - renders on entity detail pages */}
                            <EntityNavigation 
                                allClients={allClients}
                                allDepartmentIds={allDepartmentIds}
                                allStaff={allStaff}
                                clientsLoaded={clientsLoaded}
                                clientId={clientId}
                                staffId={staffId}
                                departmentId={departmentId}
                                user={user}
                                signOut={signOut}
                            />
                            
                            <Outlet context={{ 
                                apiService, 
                                clientId, 
                                clientName: getClientDisplayName(clientId),
                                clientsLoaded,
                                staffId, 
                                departmentId, 
                                dateRange,
                                setDateRange,
                                allClientIds, 
                                allClients,
                                allDepartmentIds
                            }} />
                        </EntityNavigationProvider>
                    ) : (
                        <div className="flex items-center justify-center h-64">
                           <p>Waiting for backend service connection...</p>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default Dashboard;